import React, { useContext, useState, useEffect, useCallback } from "react";
import { ModalWrapper } from "../../../../components/ui";
import { WorkerMultiSelect } from "../../../../components/ui";
import { Button } from "../../../../components/ui";
import SMSCostWarning from "./SMSCostWarning";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { formatDate } from "../../../../utils/datetime";
import { IoClose } from "react-icons/io5";
import handleSendMessages from "../../../../utils/api/messaging";
import ReviewResponseModal from "./ReviewResponseModal";

const SELECT_BY_OPTIONS = {
  assigned: "All Assigned",
  offWork: "All Off Work",
  available: "All Available",
  byJob: "By Job",
};

/**
 * Broadcast modal for sending mass SMS to selected workers.
 * User selects recipients (all active crew), enters a custom message, and sends.
 *
 * @param {Object} props
 * @param {Date} props.date - Selected day (for title)
 * @param {Object} [props.dayData] - Day-based worker sets for "Select by": assignedWorkerIds, offWorkWorkerIds, availableWorkerIds, jobsForDay
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 */
const BroadcastModal = ({ date, dayData, isOpen, onClose }) => {
  const { workers } = useContext(DispatchContext);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const [selectByOption, setSelectByOption] = useState("");
  const [selectedJobId, setSelectedJobId] = useState("");

  const data = dayData ?? {
    assignedWorkerIds: [],
    offWorkWorkerIds: [],
    availableWorkerIds: [],
    jobsForDay: [],
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedWorkerIds([]);
      setMessageText("");
      setResponse(null);
      setIsResponseOpen(false);
      setSelectByOption("");
      setSelectedJobId("");
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
    setResponse(null);
    setIsResponseOpen(false);
    setMessageText("");
  }, [onClose]);

  const selectedWorkers = workers
    ? workers.filter((w) => selectedWorkerIds.includes(w.id))
    : [];
  const messages = selectedWorkers.map((w) => ({
    worker: w,
    message: messageText.trim(),
  }));
  const canSend =
    selectedWorkerIds.length > 0 &&
    messageText.trim().length > 0 &&
    !isSending;

  const handleSendClick = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      const res = await handleSendMessages(messages);
      setResponse(res);
      setIsResponseOpen(true);
    } catch (error) {
      setResponse({
        error:
          error.response?.data?.error || "Some SMS messages failed to send",
        errors: error.response?.data?.errors || [],
        success: error.response?.data?.success || [],
      });
      setIsResponseOpen(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleSelectAll = () => {
    if (workers) setSelectedWorkerIds(workers.map((w) => w.id));
  };
  const handleClearRecipients = () => setSelectedWorkerIds([]);

  const handleReselectBy = () => {
    if (!selectByOption) return;
    if (selectByOption === "byJob" && !selectedJobId) return;
    applySelectBy(selectByOption, selectByOption === "byJob" ? selectedJobId : "");
  };

  const applySelectBy = useCallback(
    (option, jobId) => {
      if (!option) {
        setSelectedWorkerIds([]);
      } else if (option === "assigned") {
        setSelectedWorkerIds(data.assignedWorkerIds ?? []);
      } else if (option === "offWork") {
        setSelectedWorkerIds(data.offWorkWorkerIds ?? []);
      } else if (option === "available") {
        setSelectedWorkerIds(data.availableWorkerIds ?? []);
      } else if (option === "byJob" && jobId) {
        const jobEntry = data.jobsForDay?.find(
          (e) => String(e.job?.id) === String(jobId)
        );
        setSelectedWorkerIds(jobEntry?.workerIds ?? []);
      } else if (option === "byJob") {
        setSelectedWorkerIds([]);
      }
    },
    [data.assignedWorkerIds, data.offWorkWorkerIds, data.availableWorkerIds, data.jobsForDay]
  );

  if (!isOpen) return null;

  return (
    <>
      <ModalWrapper isOpen={isOpen} onClose={handleClose} closeButton={false}>
        <div className="flex flex-row justify-center">
          <h2 className="font-bold text-lg mr-4 text-center">
            Broadcast SMS for {formatDate(date, "weekday MM/DD/YY")}
          </h2>
          <div className="absolute right-6">
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={handleClose}
              type="button"
            >
              <IoClose className="m-auto text-2xl" />
            </button>
          </div>
        </div>
        {workers && workers.length > 0 && (
          <div className="w-full grid grid-cols-3 gap-2 items-center text-md mb-2 py-4 text-green-600">
            <div className="flex justify-start">
              <button
                type="button"
                onClick={handleSelectAll}
                className="hover:text-green-900 transition-all ease-in-out"
              >
                Select All
              </button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={handleReselectBy}
                className="disabled:text-black text-green-600 hover:text-green-900 cursor-pointer transition-all ease-in-out disabled:opacity-50 disabled:cursor-default"
                disabled={!selectByOption || (selectByOption === "byJob" && !selectedJobId)}
                title={
                  selectByOption && (selectByOption !== "byJob" || selectedJobId)
                    ? "Re-apply this selection"
                    : undefined
                }
              >
                Select by:
              </button>
              <select
                value={selectByOption}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectByOption(value);
                  applySelectBy(value, value === "byJob" ? selectedJobId : "");
                }}
                className="border border-gray-300 rounded px-2 py-1 text-gray-800 bg-white text-sm"
                aria-label="Select by category"
              >
                <option value="">None...</option>
                <option value="assigned">{SELECT_BY_OPTIONS.assigned}</option>
                <option value="offWork">{SELECT_BY_OPTIONS.offWork}</option>
                <option value="available">{SELECT_BY_OPTIONS.available}</option>
                <option value="byJob">{SELECT_BY_OPTIONS.byJob}</option>
              </select>
              {selectByOption === "byJob" && (
                <select
                  value={selectedJobId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedJobId(value);
                    applySelectBy("byJob", value);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-gray-800 bg-white text-sm"
                  aria-label="Select job"
                >
                  <option value="">Select job...</option>
                  {data.jobsForDay?.map((entry) => (
                    <option
                      key={entry.job?.id}
                      value={entry.job?.id}
                    >
                      {entry.job?.number} - {entry.job?.description}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClearRecipients}
                className="hover:text-green-900 transition-all ease-in-out"
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {workers ? (
          <WorkerMultiSelect
            workers={workers}
            selectedWorkerIds={selectedWorkerIds}
            onChange={setSelectedWorkerIds}
            disabledWorkerIds={[]}
            byCategory={false}
            showRoles={false}
            multiCol={true}
          />
        ) : (
          <p className="text-sm text-gray-500">Loading workers...</p>
        )}
        <p className="text-gray-500 font-bold text-md mt-4">SMS Message</p>
        <textarea
          className="w-full p-2 border bg-white border-gray-300 rounded-md text-md mt-2 min-h-[80px] resize-y"
          placeholder="Enter your message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          rows={4}
        />
        <SMSCostWarning
          messageCount={selectedWorkerIds.length}
        />
        <div
          className="mt-4"
          title={
            !messageText.trim().length
              ? "Please type a message before sending"
              : undefined
          }
        >
          <Button
            variant={canSend ? "secondary" : "disabled"}
            text={isSending ? "Sending..." : "Send Messages"}
            onClick={handleSendClick}
            disabled={!canSend}
          />
        </div>
      </ModalWrapper>
      {response && (
        <ReviewResponseModal
          response={response}
          isOpen={isResponseOpen}
          setIsOpen={setIsResponseOpen}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default BroadcastModal;
